import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { Close, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import {
  gray40,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { regularLg, regularSm, regularXl, regularXs } from '../../../constants/location';
import {
  DictionaryConversionHistory,
  DictionaryMatchResult,
  DictionaryWithMappingType,
} from '../../../types/dictionary';
import { dictionaryFuzzySearch } from '../../../api/fusejs/dictionarySearch';
import { listDictionaryHistory } from '../../../api/firebase/firestore/dictionary/history/read';
import { deleteDictionaryHistory } from '../../../api/firebase/firestore/dictionary/history/delete';
import DeleteModal from './DeleteModal';

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      padding: '32px',
      margin: '0',
      width: '70vw',
      '@media screen and (max-width:960px)': {
        padding: '16px',
        width: '100%',
        margin: '16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  dialogTitle: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  iconButton: {
    padding: 0,
  },
  dialogCloseButton: {
    height: '32px',
    width: '32px',
  },
  tableContainer: {
    minWidth: '100%',
    overflowX: 'auto',
    margin: '0px',
    padding: '0px',
    border: '1px solid #E0E0E0',
  },
  tableHeader: {
    padding: '8px 10px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '35px',
    borderRight: '1px solid #E0E0E0',
  },
  // スコア(一致度)など短いテキスト向けのヘッダー。横幅を最小限に詰める。
  tableHeaderNarrow: {
    padding: '8px 10px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    width: '1%',
    borderRight: '1px solid #E0E0E0',
  },
  tableCell: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px',
    whiteSpace: 'nowrap',
    minWidth: '120px',
    borderRight: '1px solid #E0E0E0',
  },
  // スコア(一致度)など短いテキスト向け。横幅を最小限に詰める。
  tableCellNarrow: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px',
    whiteSpace: 'nowrap',
    width: '1%',
    borderRight: '1px solid #E0E0E0',
  },
  buttonCell: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    padding: '8px 10px',
    width: '44px',
    borderRight: '1px solid #E0E0E0',
  },
  selectButton: {
    display: 'flex',
    width: '44px',
    height: '32px',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: '16px',
  },
  // 変換履歴タブの操作セル（選択/削除）。照合履歴タブと同じデザイン。
  actionCell: {
    padding: '8px 10px',
    width: '1%',
    whiteSpace: 'nowrap',
    borderRight: '1px solid #E0E0E0',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  actionButton: {
    height: '32px',
    minWidth: '44px',
    whiteSpace: 'nowrap',
  },
  emptyMessage: {
    ...regularSm,
    color: gray80,
    textAlign: 'center',
    padding: '32px 0',
  },
  textField: {
    '& .MuiInputBase-root': { height: '40px', fontSize: '14px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
      borderWidth: '1px',
    },
  },
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (result: DictionaryMatchResult) => void;
  /** 辞書変換を解除する（変換項目を OCR 値に戻す）。設定時のみ「変換解除」ボタンを表示。 */
  onUnlink?: () => void;
  dictionary: DictionaryWithMappingType | null;
  /** 元のOCR値（フォールバック用） */
  ocrValue?: string;
  /** 初期検索クエリ。入力欄で編集中の現在値。未指定なら ocrValue を使う。 */
  searchQuery?: string;
}

const DictionaryReconciliationModal = ({
  open,
  onClose,
  onSelect,
  onUnlink,
  dictionary,
  ocrValue = '',
  searchQuery,
}: Props): ReactElement => {
  const [searchWord, setSearchWord] = useState('');
  const [results, setResults] = useState<DictionaryMatchResult[]>([]);
  // タブ: 0=辞書変換（あいまい検索）, 1=変換履歴
  const [activeTab, setActiveTab] = useState(0);
  const [historyList, setHistoryList] = useState<DictionaryConversionHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DictionaryConversionHistory | null>(null);
  const [deletingHistory, setDeletingHistory] = useState(false);

  // テーブルの列順: 検索キー列(search_csv_column) → マッピング対象列 → その他の残りカラム。
  // 辞書の csv_columns には全カラムが含まれるので、優先列を先頭に並べ替えるだけで構築する。
  const csvHeaders = useMemo<string[]>(() => {
    if (!dictionary) return [];
    const primary = dictionary.search_csv_column ? [dictionary.search_csv_column] : [];
    const mapped = (dictionary.column_mappings ?? [])
      .map(m => m.csv_column)
      .filter(col => col && col !== dictionary.search_csv_column);
    const knownSet = new Set<string>([...primary, ...mapped]);
    const others = (dictionary.csv_columns ?? []).filter(col => !knownSet.has(col));
    return [...primary, ...mapped, ...others];
  }, [dictionary]);

  const handleSearch = useCallback(
    (query: string): void => {
      if (!dictionary) {
        setResults([]);
        return;
      }
      setResults(dictionaryFuzzySearch(dictionary.rows, dictionary.search_csv_column, query));
    },
    [dictionary],
  );

  // モーダルが開いたとき、入力欄の現在値（searchQuery）を初期クエリにして自動検索する。
  // 未指定なら元のOCR値（ocrValue）を使う。
  React.useEffect(() => {
    if (!open) return;
    const initialQuery = searchQuery ?? ocrValue;
    setSearchWord(initialQuery);
    handleSearch(initialQuery);
  }, [open, searchQuery, ocrValue, handleSearch]);

  const handleSelect = (result: DictionaryMatchResult): void => {
    onSelect(result);
    onClose();
  };

  // モーダルを開いたらタブを「辞書変換」に戻し、変換履歴を先読みする。
  const loadHistory = useCallback(async (): Promise<void> => {
    if (!dictionary) {
      setHistoryList([]);
      return;
    }
    setLoadingHistory(true);
    try {
      setHistoryList(await listDictionaryHistory(dictionary.dictionary_id));
    } catch {
      setHistoryList([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [dictionary]);

  React.useEffect(() => {
    if (!open) return;
    setActiveTab(0);
    void loadHistory();
  }, [open, loadHistory]);

  // 変換履歴の行を選択 → その確定行を結果に反映する（辞書変換の選択と同じ反映経路）。
  const handleHistorySelect = (record: DictionaryConversionHistory): void => {
    onSelect({
      rowData: record.matched_row,
      matchedValue: record.matched_value,
      score: 0,
      matchRate: 1, // 履歴からの確定は完全一致扱い（100%）
    });
    onClose();
  };

  const handleHistoryDelete = async (): Promise<void> => {
    if (!dictionary || !deleteTarget) return;
    setDeletingHistory(true);
    try {
      await deleteDictionaryHistory(dictionary.dictionary_id, deleteTarget.history_id);
      setDeleteTarget(null);
      await loadHistory();
    } finally {
      setDeletingHistory(false);
    }
  };

  // 一致度(0〜1)を「一致度(%)」表示に変換する。
  // matchRate は fuse.js のスコアに文字数差の補正を掛けた値（calcDictionaryMatchRate）。
  const toMatchRate = (matchRate: number): string => `${Math.round(matchRate * 100)}%`;

  return (
    <Dialog open={open} sx={styles.dialog} maxWidth={false} fullWidth={true}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          {dictionary?.file_name || '辞書'} - 辞書変換
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onUnlink && (
            <Button
              sx={{ ...secondaryButtonBackgroundColor, height: '32px', whiteSpace: 'nowrap' }}
              onClick={onUnlink}
            >
              変換解除
            </Button>
          )}
          <IconButton onClick={onClose} sx={styles.iconButton}>
            <Close sx={styles.dialogCloseButton} />
          </IconButton>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v): void => setActiveTab(v as number)}
        sx={{
          mb: '16px',
          borderBottom: '1px solid #E0E0E0',
          '& .MuiTabs-indicator': { backgroundColor: gray100 },
          '& .MuiTab-root.Mui-selected': { color: gray100 },
          '& .MuiTab-root': { color: gray80 },
        }}
      >
        <Tab label="辞書変換" />
        <Tab label="変換履歴" />
      </Tabs>

      {/* タブ0: 辞書変換（あいまい検索） */}
      {activeTab === 0 && (
        <>
          <Typography sx={{ ...regularXs, color: gray80, mb: '12px' }}>
            辞書データからあいまい一致で候補を絞り込みます。該当する結果を選択してください。
          </Typography>

          <Box sx={{ display: 'flex', gap: '12px', mb: '16px', alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <Typography sx={{ ...regularSm, color: gray80 }}>
                {dictionary?.search_item_label ?? '検索キーワード'}（あいまい一致）
              </Typography>
              <TextField
                size="small"
                value={searchWord}
                onChange={(e): void => setSearchWord(e.target.value)}
                onKeyDown={(e): void => {
                  if (e.key === 'Enter') handleSearch(searchWord);
                }}
                placeholder="例: 東亜"
                sx={styles.textField}
              />
            </Box>
            <Button
              size="small"
              startIcon={<Search />}
              sx={{ ...primaryButtonBackgroundColor, height: '40px' }}
              onClick={(): void => handleSearch(searchWord)}
            >
              検索
            </Button>
          </Box>

          {results.length === 0 ? (
            <Typography sx={styles.emptyMessage}>該当する候補が見つかりませんでした</Typography>
          ) : (
            <TableContainer sx={styles.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.tableHeader}></TableCell>
                    <TableCell sx={styles.tableHeaderNarrow}>一致度</TableCell>
                    {csvHeaders.map((header, index) => (
                      <TableCell key={index} sx={styles.tableHeader}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result, index) => (
                    <TableRow key={index}>
                      <TableCell align="center" sx={styles.buttonCell}>
                        <Button
                          sx={[secondaryButtonBackgroundColor, styles.selectButton]}
                          onClick={(): void => handleSelect(result)}
                        >
                          選択
                        </Button>
                      </TableCell>
                      <TableCell sx={styles.tableCellNarrow}>
                        {toMatchRate(result.matchRate)}
                      </TableCell>
                      {csvHeaders.map((header, colIndex) => (
                        <TableCell key={colIndex} sx={styles.tableCell}>
                          {result.rowData[header] ?? ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* タブ1: 変換履歴（この辞書で過去に確定した「読取結果 → 変換したワード」の一覧） */}
      {activeTab === 1 && (
        <>
          <Typography sx={{ ...regularXs, color: gray80, mb: '12px' }}>
            この辞書で過去に変換した履歴です。行を選択すると反映できます。誤った変換は削除してください。
          </Typography>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
              <CircularProgress size={32} sx={{ color: gray80 }} />
            </Box>
          ) : historyList.length === 0 ? (
            <Typography sx={styles.emptyMessage}>変換履歴はありません</Typography>
          ) : (
            <TableContainer sx={styles.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.tableHeader}></TableCell>
                    <TableCell sx={styles.tableHeader}>読取結果</TableCell>
                    <TableCell sx={styles.tableHeader}>変換したワード</TableCell>
                    {csvHeaders
                      .filter(h => h !== dictionary?.search_csv_column)
                      .map((header, index) => (
                        <TableCell key={index} sx={styles.tableHeader}>
                          {header}
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyList.map((record, index) => (
                    <TableRow key={index}>
                      <TableCell sx={styles.actionCell}>
                        <Box sx={styles.actionButtons}>
                          <Button
                            sx={[secondaryButtonBackgroundColor, styles.actionButton]}
                            onClick={(): void => handleHistorySelect(record)}
                          >
                            選択
                          </Button>
                          <Button
                            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
                            onClick={(): void => setDeleteTarget(record)}
                          >
                            削除
                          </Button>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.tableCell}>{record.query_text}</TableCell>
                      <TableCell sx={styles.tableCell}>{record.matched_value}</TableCell>
                      {csvHeaders
                        .filter(h => h !== dictionary?.search_csv_column)
                        .map((header, colIndex) => (
                          <TableCell key={colIndex} sx={styles.tableCell}>
                            {record.matched_row?.[header] ?? ''}
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <DeleteModal
        open={deleteTarget !== null}
        onClose={(): void => setDeleteTarget(null)}
        onDelete={(): void => void handleHistoryDelete()}
        title="変換履歴"
        loading={deletingHistory}
        description={
          deleteTarget
            ? `読取結果「${deleteTarget.query_text}」と「${deleteTarget.matched_value}」の変換履歴を削除します。よろしいですか？`
            : undefined
        }
      />
    </Dialog>
  );
};

export default DictionaryReconciliationModal;
